"use client"

import { useState } from "react"
import { FONT_SANS } from "@/lib/constants"
import type { NoSlotRequest } from "@/app/rdv/_types"

export interface NoSlotResult {
  alreadyRegistered: boolean
}

/**
 * Bouton "Aucun créneau ne me convient" — sobre, secondaire, visuellement
 * distinct de la liste des créneaux sélectionnables. Poste la trace NO_SLOT
 * puis remonte le résultat au parent via onDone (le parent affiche la
 * confirmation via NoSlotConfirmation).
 */
export function NoSlotButton({
  payload,
  onDone,
  disabled,
}: {
  payload: NoSlotRequest
  onDone: (result: NoSlotResult) => void
  disabled?: boolean
}) {
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  async function handleClick() {
    setLoading(true)
    setError(null)
    try {
      const res = await fetch("/api/rdv", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ noSlot: true, ...payload }),
      })
      const data = (await res.json().catch(() => ({}))) as {
        error?: string
        alreadyRegistered?: boolean
      }
      if (!res.ok) {
        setError(data.error ?? "Une erreur est survenue, veuillez réessayer.")
        setLoading(false)
        return
      }
      onDone({ alreadyRegistered: data.alreadyRegistered === true })
    } catch {
      setError("Une erreur est survenue, veuillez réessayer.")
      setLoading(false)
    }
  }

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
      <button
        onClick={handleClick}
        disabled={disabled || loading}
        style={{
          background: "transparent",
          border: "1px solid var(--ljs-border)",
          borderRadius: 10,
          padding: "10px 16px",
          fontFamily: FONT_SANS,
          fontSize: 13,
          fontWeight: 600,
          color: "var(--ljs-text-dim)",
          cursor: disabled || loading ? "not-allowed" : "pointer",
          opacity: disabled || loading ? 0.6 : 1,
          transition: "all 0.12s",
        }}
      >
        {loading ? "Enregistrement…" : "Aucun créneau ne me convient"}
      </button>
      {error && (
        <p style={{ fontFamily: FONT_SANS, fontSize: 12, color: "#EF4444", margin: 0 }}>{error}</p>
      )}
    </div>
  )
}

/**
 * Écran de confirmation après un NO_SLOT — message identique quelle que soit
 * la provenance (flux classique / lien SMS). alreadyRegistered adapte le texte
 * quand la demande existait déjà (règle d'unicité).
 */
export function NoSlotConfirmation({ alreadyRegistered }: { alreadyRegistered: boolean }) {
  return (
    <div
      style={{
        background: "rgba(14,165,233,0.06)",
        border: "1px solid rgba(14,165,233,0.2)",
        borderRadius: 12,
        padding: "24px 20px",
        textAlign: "center",
      }}
    >
      <span style={{ fontSize: 32, display: "block", marginBottom: 10 }}>✓</span>
      <p
        style={{
          fontFamily: FONT_SANS,
          fontSize: 15,
          fontWeight: 700,
          color: "var(--ljs-text)",
          margin: "0 0 6px",
        }}
      >
        {alreadyRegistered
          ? "Votre demande est déjà enregistrée"
          : "Votre demande a bien été enregistrée"}
      </p>
      <p
        style={{
          fontFamily: FONT_SANS,
          fontSize: 13,
          color: "var(--ljs-text-dim)",
          lineHeight: 1.6,
          margin: 0,
        }}
      >
        LJS Plomberie vous contactera prochainement pour convenir d&apos;un rendez-vous.
      </p>
    </div>
  )
}
